import '@klokhuis/bootstrap';

import React from 'react';

import NextApp, {
  AppContext as NextAppContext,
  AppProps as NextAppProps,
  NextWebVitalsMetric,
} from 'next/app';
import Head from 'next/head';

import { EmotionCache } from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { Translations } from '@freshbuffer/intl';
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';
import { GoogleAnalytics } from '@klokhuis/core/Analytics/GoogleAnalytics';
import { Hotjar } from '@klokhuis/core/Analytics/Hotjar';
import {
  identify,
  page,
  track,
} from '@klokhuis/core/Analytics/libs/analytics-lib';
import {
  AnalyticsEvent,
  AnalyticsPageCategory,
  AnalyticsPageName,
  AnalyticsPageProps,
  AnalyticsUserProperties,
} from '@klokhuis/core/Analytics/types';
import {
  getPageEventPayload,
  getPageViewedEventPayload,
  getUserProperties,
} from '@klokhuis/core/Analytics/utilities';
import { AppServicesProvider } from '@klokhuis/core/AppServices';
import { AuthProvider } from '@klokhuis/core/Auth/AuthProvider';
import { initAuthBrowserApp } from '@klokhuis/core/Auth/initAuthBrowserApp';
import { initAuthServerApp } from '@klokhuis/core/Auth/initAuthServerApp';
import { IAuthState } from '@klokhuis/core/Auth/types';
import { CommonMessages } from '@klokhuis/core/CommonMessages';
import { setUserContextForErrorReporting } from '@klokhuis/core/Errors';
import { ErrorBoundary } from '@klokhuis/core/Errors/ErrorBoundary';
import { initFeatureFlagsBrowserApp } from '@klokhuis/core/FeatureFlags/initFeatureFlagsBrowserApp';
import {
  initSessionFeatureFlagsServerApp,
  initUserFeatureFlagsServerApp,
} from '@klokhuis/core/FeatureFlags/initFeatureFlagsServerApp';
import { FeatureFlags } from '@klokhuis/core/FeatureFlags/types';
import { IntlProvider, createIntl } from '@klokhuis/core/Intl';
import { Locale } from '@klokhuis/core/Intl/types';
import { logger } from '@klokhuis/core/Logger';
import { createMuiDateLocalizationAdapter } from '@klokhuis/core/MUI/MuiDateLocalizationAdapter';
import { PUBLIC_ROUTES, ROUTES } from '@klokhuis/core/Routing';
import { createEmotionCache } from '@klokhuis/styles/createEmotionCache';

// Poppins weights used by MUI theme: 300 (h2), 400 (body), 500 (h6), 600, 700 (h1)
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
// Inter weights used by MUI theme: 400 (body), 500 (subtitle), 600, 700 (bold)
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@klokhuis/styles/GlobalStyles.css';
import '@klokhuis/styles/normalize.css';
import '@klokhuis/styles/theme.design.css';

import { makeTheme } from '@klokhuis/styles/theme.mui';

import '@klokhuis/styles/theme.mui.css';

import { getDeviceType } from '@klokhuis/utilities/UserAgent';
import { DeviceType } from '@klokhuis/utilities/UserAgent/types';

import appleTouchIcon from '../../public/apple-touch-icon.png';
import favicon from '../../public/android-chrome-192x192.png';

import { loadLocale } from '../locales';

const clientSideEmotionCache = createEmotionCache();

function sendRudderstackPageEvent(
  pageProps: PageProps,
  locale: Locale,
  userProperties: AnalyticsUserProperties,
): void {
  const pageEventPayload = getPageEventPayload(
    locale,
    pageProps.analytics?.properties,
    userProperties,
  );

  page(
    pageProps.analytics?.category || AnalyticsPageCategory.Other,
    pageProps.analytics?.name || AnalyticsPageName.Other,
    pageEventPayload,
    {
      integrations: {
        Mixpanel: false,
      },
    },
  );
}

/**
 * This event is only used for Mixpanel because Klokhuis tracks page viewed events as "PageViewed". This is why we can't use sendRudderstackPageEvent(), which uses "Loaded a page" instead.
 */
function sendMixpanelPageEvent(
  pageProps: PageProps,
  locale: Locale,
  userProperties: AnalyticsUserProperties,
): void {
  const pageViewedEventPayload = getPageViewedEventPayload(
    locale,
    pageProps.analytics?.name,
    pageProps.analytics?.category,
    pageProps.analytics?.properties,
    userProperties,
  );

  track(AnalyticsEvent.PageViewed, pageViewedEventPayload, {
    integrations: { All: false, Mixpanel: true },
  });
}

type PageProps = AnalyticsPageProps & Record<string, any>;

export interface AppProps extends NextAppProps<PageProps> {
  emotionCache?: EmotionCache;
  messages: Translations;
  authState: IAuthState;
  featureFlags: FeatureFlags;
  pageProps: PageProps;
  deviceType: DeviceType;
}

const App = ({
  Component,
  emotionCache = clientSideEmotionCache,
  pageProps,
  deviceType,
  authState,
  featureFlags,
  messages,
  router,
}: AppProps) => {
  const intl = React.useMemo(() => {
    const locale = Locale[router.locale as Locale] || Locale.ta;

    return createIntl({
      locale,
      messages,
    });
  }, [messages, router.locale]);

  const { T, locale } = intl;
  const dateAdapter = React.useMemo(
    () => createMuiDateLocalizationAdapter(),
    [],
  );
  const theme = React.useMemo(
    () => makeTheme(locale, deviceType),
    [deviceType, locale],
  );

  const userId = authState?.userProfile?.userid;
  const userMembershipName = authState?.userProfile?.productshortdescription;

  React.useEffect(() => {
    const userProperties = getUserProperties();
    if (userId) {
      identify(`u${userId}`, userProperties);
      if (authState.userProfile) {
        setUserContextForErrorReporting(
          authState.userProfile,
          locale,
          authState.anonymousId,
        );
      }
    }

    sendMixpanelPageEvent(pageProps, locale, userId ? {} : userProperties);
    sendRudderstackPageEvent(pageProps, locale, userId ? {} : userProperties);
  }, [pageProps, userId, locale, userMembershipName, authState]);

  return (
    <ErrorBoundary T={T} locale={locale}>
      <React.Fragment>
        <Head>
          <meta
            name="viewport"
            content="minimum-scale=1, initial-scale=1, width=device-width"
          />
        </Head>

        <Hotjar />
        <GoogleAnalytics />
        <VercelAnalytics />

        <IntlProvider value={intl}>
          <Head>
            <title>{T(CommonMessages.app_name)}</title>
            <meta
              name="apple-mobile-web-app-title"
              content={T(CommonMessages.app_name)}
            />
            <meta
              name="application-name"
              content={T(CommonMessages.app_name)}
            />
            <link
              rel="icon"
              href={favicon.src}
              type="image/png"
              sizes="192x192"
            />
            <link rel="apple-touch-icon" href={appleTouchIcon.src} />
          </Head>

          <AuthProvider authState={authState}>
            <AppServicesProvider featureFlags={featureFlags}>
              <CacheProvider value={emotionCache}>
                <ThemeProvider theme={theme}>
                  <StyledEngineProvider>
                    <LocalizationProvider
                      dateAdapter={dateAdapter}
                      adapterLocale={locale}
                    >
                      <Component {...pageProps} />
                    </LocalizationProvider>
                  </StyledEngineProvider>
                </ThemeProvider>
              </CacheProvider>
            </AppServicesProvider>
          </AuthProvider>
        </IntlProvider>
      </React.Fragment>
    </ErrorBoundary>
  );
};

// opt-out of Automatic Static Optimization
App.getInitialProps = async (
  appContext: NextAppContext,
): Promise<
  Pick<
    AppProps,
    'messages' | 'authState' | 'pageProps' | 'featureFlags' | 'deviceType'
  >
> => {
  const { ctx } = appContext;
  const locale = Locale[ctx.locale as Locale] || Locale.ta;
  const route = ctx.pathname;
  const startTime = Date.now();

  logger.info({
    message: `_app: processing route ${route} on ${
      ctx.req ? 'server' : 'browser'
    } ; started`,
    route,
    locale,
    query: ctx.query,
  });

  const deviceType: DeviceType = appContext.ctx.req
    ? getDeviceType(appContext.ctx.req.headers['user-agent'])
    : appContext.router.components[appContext.router.route]?.props
        ?.deviceType || 'desktop';

  let messages: Record<string, string> = {};

  const sessionFeatureFlags = ctx.req
    ? await initSessionFeatureFlagsServerApp(appContext)
    : initFeatureFlagsBrowserApp(appContext);

  const authState = ctx.req
    ? await initAuthServerApp(appContext, sessionFeatureFlags)
    : initAuthBrowserApp(appContext);

  const userFeatureFlags = ctx.req
    ? await initUserFeatureFlagsServerApp(appContext)
    : initFeatureFlagsBrowserApp(appContext);

  const featureFlags = {
    ...userFeatureFlags,
    ...sessionFeatureFlags,
  };

  logger.info({
    message: `_app: processing route ${route} ; initialized auth state`,
    route,
    locale,
    query: ctx.query,
    duration: Date.now() - startTime,
  });

  const initialProps = await NextApp.getInitialProps(appContext);

  logger.info({
    message: `_app: processing route ${route} ; loaded props`,
    route,
    locale,
    query: ctx.query,
    initialProps,
    duration: Date.now() - startTime,
  });

  const duration = Date.now() - startTime;

  if (ctx.req && duration > 800) {
    logger.warn({
      message: `_app: processing route ${route} ; loaded props with degraded performance`,
      route,
      locale,
      query: ctx.query,
      duration,
    });
  }

  function redirect(url: string) {
    logger.info({
      message: `_app: processing route ${route} ; done ; redirecting from "${ctx.asPath}" to "${url}"`,
      duration: Date.now() - startTime,
    });

    if (ctx.req) {
      ctx.res!.writeHead(307, {
        Location: url,
      });
      ctx.res!.end();
    } else {
      window.location.assign(url);
    }

    return {
      messages: {},
      authState,
      featureFlags,
      pageProps: {},
      deviceType,
    };
  }

  if (!route.startsWith('/_') && route !== '/404' && route !== '/500') {
    if (!authState.accessToken && !PUBLIC_ROUTES.includes(route)) {
      const signinUrl = ROUTES.SignupSignin.url({});

      return redirect(signinUrl);
    }
  }

  if (route !== '/_error') {
    messages = await loadLocale(locale);
  }

  logger.info({
    message: `_app: processing route ${route} ; done`,
    route,
    query: ctx.query,
    initialProps,
    duration: Date.now() - startTime,
  });

  return {
    messages,
    authState,
    featureFlags,
    ...initialProps,
    deviceType,
  };
};

export function reportWebVitals(metric: NextWebVitalsMetric) {
  logger.info({
    message: `Reporting web vital metric ${metric.name}`,
    ...metric,
  });

  const { label, name, value, id } = metric;

  track(
    name,
    {
      category: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
      klokhuisApp: 'next',
      name,
      value: Math.round(name === 'CLS' ? value * 1000 : value),
      event_label: id,
      non_interaction: true,
    },
    {
      event_label: id,
      non_interaction: true,
    },
  );
}

export default App;
